import React, { useEffect, useRef, useState } from "react";
import { Area, ComposedChart, Label, ReferenceLine, Tooltip, XAxis, YAxis } from "recharts";
import moment from "moment";
import cropr from "../../shared/utils/cropr";
import gettext from "../../shared/utils/gettext";
import { interpolate } from "../../shared/utils/legacy";
import { Button, ButtonGroup, Spinner, ToggleButton, ToggleButtonGroup } from "react-bootstrap";

function getStatsInfo() {
  const statsInfo = {
    ndvi: {
      description: interpolate(gettext("show the %s visualization"), [
        "NDVI (Normalized Difference Vegetation Index)",
      ]),
      abr: "NDVI",
    },
    wdvi: {
      description: interpolate(gettext("show the %s visualization"), [
        "WDVI (Weighted Difference Vegetation Index)",
      ]),
      abr: "WDVI",
    },
  };

  if (cropr.getConfig("experiments", "extra_sat_indices", true)) {
    Object.assign(statsInfo, {
      leaf_n_corn: {
        description: interpolate(gettext("show the %s visualization"), ["LEAF_N_CORN"]),
        abr: "LNC",
      },
      leaf_n_potato: {
        description: interpolate(gettext("show the %s visualization"), ["LEAF_N_POTATO"]),
        abr: "LNP",
      },
      msi: {
        description: interpolate(gettext("show the %s visualization"), [
          "MSI (Moisture Stress Index)",
        ]),
        abr: "MSI",
      },
      savi: {
        description: interpolate(gettext("show the %s visualization"), [
          "SAVI (Landsat Soil Adjusted Vegetation Index)",
        ]),
        abr: "SAVI",
      },
    });
  }
  if (cropr.getConfig("experiments", "sebal_indices", true)) {
    Object.assign(statsInfo, {
      et24: {
        description: interpolate(gettext("show the %s visualization"), ["ET24"]),
        abr: "ET",
      },
      kc: {
        description: interpolate(gettext("show the %s visualization"), ["KC"]),
        abr: "KC",
      },
      rm: {
        description: interpolate(gettext("show the %s visualization"), ["RM"]),
        abr: "RM",
      },
    });
  }
  return statsInfo;
}

function getStatsTypes() {
  const statsInfo = getStatsInfo();
  return Object.keys(statsInfo);
}

function SatView(props) {
  const { cropfield, setImageLocation } = props;
  const wkt = cropfield && cropfield.get("geometry");
  const [data, setData] = useState();
  const [validData, setValidData] = useState();
  const [fieldId, setFieldId] = useState();
  const [date, setDate] = useState();
  const [startDate, setStartDate] = useState(moment().subtract(1, "years").format("YYYYMMDD"));
  const [imgType, setImgType] = useState("ndvi");
  const [buffer, setBuffer] = useState(0);
  const [adjust, setAdjust] = useState(false);
  const [smooth, setSmooth] = useState(false);
  const [sp, setSp] = useState(false);
  const [spLoading, setSpLoading] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const statsTypes = getStatsTypes();
  const statsInfo = getStatsInfo();

  useEffect(() => {
    setImageLocation(null);
    return function reset() {
      setImageLocation(null);
    };
  }, [setImageLocation]);
  useEffect(() => {
    if (!wkt) {
      return;
    }
    const url = `${window.origin}/api/v3/satellite/statistics/`;
    const st = imgType === "rgb" ? "ndvi" : imgType;
    fetch(url, {
      method: "POST",
      body: JSON.stringify({
        wkt: wkt,
        start_date: startDate,
        all: "true",
        types: st.toUpperCase(),
      }),
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        "X-CSRFToken": cropr.getCookie("csrftoken"),
      },
    }).then((response) => {
      if (response.ok) {
        response.json().then((result) => {
          const content = result.content;
          const d = content.dates;
          const data = Object.keys(d)
            .sort()
            .map((key) => {
              const value = d[key],
                stats = value.statistics,
                entry = {
                  unix: moment(key).unix(),
                  year: moment(key).format("YYYY"),
                  valid: value.meta.valid,
                };
              const stat = stats[st.toUpperCase()];
              entry[`${st}_average`] = stat.average;
              entry[`${st}_std`] = stat.std;
              entry[`${st}_low`] = [stat.average - stat.std, stat.average];
              entry[`${st}_high`] = [stat.average + stat.std, stat.average];
              return entry;
            });
          setFieldId(content.meta.field_id);
          const validData = data.filter((d) => d.valid);
          setValidData(validData);
          setData(data);
        });
      } else {
        setData(null);
        setValidData(null);
        cropr.notify("error", gettext("Can't get satellite data for this crop field."));
      }
    });
  }, [wkt, startDate, imgType]);
  useEffect(() => {
    if (data && data.length && (!date || !data.find((d) => d.unix === moment(date).unix()))) {
      const vData = data.filter((d) => d.valid);
      if (vData.length) {
        setDate(moment.unix(vData.at(vData.length - 1).unix).format("YYYYMMDD"));
      } else {
        setDate(moment.unix(data.at(vData.length - 1).unix).format("YYYYMMDD"));
      }
    }
  }, [data, date]);
  useEffect(() => {
    if (!wkt || !date || !imgType) {
      return;
    }
    let imageType = imgType;
    if (sp) {
      imageType = "sp";
      setSpLoading(true);
    }
    const url = `${window.origin}/api/v3/satellite/images/`;
    const body = {
      wkt: wkt,
      date: date,
      legend: imageType,
      adjust: adjust ? "true" : "false",
    };
    if (buffer && !sp) {
      body.buffer = buffer;
    }
    fetch(url, {
      method: "POST",
      body: JSON.stringify(body),
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        "X-CSRFToken": cropr.getCookie("csrftoken"),
      },
    }).then((response) => {
      setSpLoading(false);
      if (response.ok) {
        response.json().then((result) => {
          const content = result.content;
          const imageLocation = content.images[date][`${imageType}.png`];
          const url = new URL(`${window.origin}/api/v3/satellite/image/${imageLocation}`);
          smooth && url.searchParams.append("smooth", "true");
          url.searchParams.append("image_size", "400");
          setImageLocation({
            type: "image",
            url: url.toString(),
            bounds: [
              content.bounding_box[0][0],
              content.bounding_box[1][1],
              content.bounding_box[2][0],
              content.bounding_box[0][1],
            ],
            zoomTo: !buffer,
            opacity: 1,
            key: imageLocation,
            legend: content.legends && content.legends[imageType],
            name: imageType.toUpperCase(),
            display_name: sp ? gettext("Soil potential") : imageType.toUpperCase(),
            date: date,
            data_type: "satellite",
            cropfield: cropfield,
          });
        });
      } else {
        cropr.notify("error", gettext("Can't get satellite image for this crop field."));
      }
    });
  }, [fieldId, date, imgType, buffer, adjust, smooth, setImageLocation, cropfield, sp]);

  return (
    <div>
      {data && data.length ? (
        <>
          <div className="bg-white p-1">
            <h5>&nbsp;</h5>
            {validData.length > 0 ? (
              <ComposedChart
                width={405}
                height={300}
                data={validData}
                onClick={(clickData) => {
                  if (!clickData) return;
                  setDate(
                    moment.unix(validData[clickData.activeTooltipIndex].unix).format("YYYYMMDD")
                  );
                }}
                allowDataOverflow
              >
                <XAxis
                  dataKey="unix"
                  tickFormatter={(unix) => moment.unix(unix).format("DD-MM-YYYY")}
                  type="number"
                  scale="time"
                  domain={
                    data.length
                      ? [data[0].unix, data[data.length - 1].unix]
                      : [moment(startDate).unix, moment().unix]
                  }
                />
                <YAxis />
                <Tooltip content={<GrowthTooltip />} />
                <Area
                  type="monotone"
                  dataKey={`${imgType === "rgb" ? "ndvi" : imgType}_high`}
                  stroke="#ffc341"
                  fill="#ffc341"
                  name="ndvi+"
                  connectNulls
                />
                <Area
                  type="monotone"
                  dataKey={`${imgType === "rgb" ? "ndvi" : imgType}_low`}
                  stroke="#eeb230"
                  fill="#eeb230"
                  name="ndvi-"
                  connectNulls
                />
                {date && data.length && (
                  <ReferenceLine x={moment(date).unix()} stroke="#A9B6BA">
                    <Label
                      value={moment(date).format("LL")}
                      position={
                        moment(date).unix() > (data[data.length - 1].unix + data[0].unix) / 2
                          ? "insideTopRight"
                          : "insideTopLeft"
                      }
                    />
                  </ReferenceLine>
                )}
              </ComposedChart>
            ) : (
              <div className="alert alert-info pt-2 mt-2">
                {gettext("No valid NDVI data is available for this location")}
              </div>
            )}
            <div className="pb-1">
              <ToggleButtonGroup
                size="sm"
                value={imgType}
                name="imageType"
                onChange={setImgType}
                className="pl-1 pb-1"
              >
                {statsTypes.map((st) => (
                  <ToggleButton
                    key={st}
                    value={st}
                    name="imageType"
                    title={statsInfo[st].description}
                    variant={imgType === st ? "primary" : "secondary"}
                  >
                    {statsInfo[st].abr}
                  </ToggleButton>
                ))}
                <ToggleButton
                  value="rgb"
                  name="imageType"
                  variant={imgType === "rgb" ? "primary" : "secondary"}
                  title={gettext("show the colour image")}
                >
                  RGB
                </ToggleButton>
              </ToggleButtonGroup>
              <ButtonGroup toggle className="pl-1 pb-1" size="sm">
                <ToggleButton
                  type="checkbox"
                  variant={buffer ? "primary" : "secondary"}
                  checked={buffer}
                  value={buffer}
                  onChange={(e) => setBuffer(e.currentTarget.checked ? 1000 : 0)}
                  title={gettext("show the surroundings of the field up to 1 kilometer")}
                >
                  +1km
                </ToggleButton>
              </ButtonGroup>
              <ButtonGroup toggle className="pl-1 pb-1" size="sm">
                <ToggleButton
                  type="checkbox"
                  variant={adjust ? "primary" : "secondary"}
                  checked={adjust}
                  value={adjust}
                  onChange={(e) => setAdjust(e.currentTarget.checked)}
                  title={gettext("adjust the legend to fit the NDVI values on the field")}
                >
                  &nbsp;
                  <i className="far fa-adjust"> </i>&nbsp;
                </ToggleButton>
              </ButtonGroup>
              <ButtonGroup toggle className="pl-1 pb-1" size="sm">
                <ToggleButton
                  type="checkbox"
                  variant={smooth ? "primary" : "secondary"}
                  checked={smooth}
                  value={smooth}
                  onChange={(e) => setSmooth(e.currentTarget.checked)}
                  title={gettext("smooth the colors of the image")}
                >
                  &nbsp;
                  <i className="far fa-hand-point-up"> </i>&nbsp;
                </ToggleButton>
              </ButtonGroup>
              <ButtonGroup toggle className="pl-1 pb-1" size="sm">
                <ToggleButton
                  type="checkbox"
                  variant={sp ? "primary" : "secondary"}
                  checked={sp}
                  value={sp}
                  onChange={(e) => setSp(e.currentTarget.checked)}
                  title={gettext("show soil potential map")}
                >
                  &nbsp;
                  <i className={spLoading ? "far fa-circle-notch fa-spin" : "far fa-bolt"}> </i>
                  &nbsp;
                </ToggleButton>
              </ButtonGroup>
            </div>
          </div>
          <div
            className="fixed-bottom bg-transparent"
            style={{
              bottom: collapsed ? "-120px" : "0",
              transition: "all 0.2s ease",
            }}
          >
            <div
              className="container-fluid row flex-row flex-nowrap p-2 m-0"
              style={{ overflowX: "auto" }}
            >
              <Button
                className="mt-4 mb-1"
                variant="outline-primary"
                title={gettext("load more satellite images")}
                onClick={() => {
                  setStartDate(moment(startDate).subtract(1, "years").format("YYYYMMDD"));
                }}
              >
                <i className="far fa-plus"> </i>
              </Button>
              <button
                className="btn btn-circle btn-smoke position-absolute"
                style={{ top: "-15px", left: "50%" }}
                onClick={() => setCollapsed(!collapsed)}
              >
                <i className={"far " + (collapsed ? "fa-chevron-up" : "fa-chevron-down")}> </i>
              </button>

              {data.map((d, i) => (
                <SatCard
                  data={d}
                  onClick={setDate}
                  selected={date === moment.unix(d.unix).format("YYYYMMDD")}
                  fieldId={fieldId}
                  imgType={imgType}
                  key={fieldId + d.unix + imgType}
                  showYearR={!collapsed && (i === data.length - 1 || data[i + 1].year !== d.year)}
                  showYearL={!collapsed && (i === 0 || data[i - 1].year !== d.year)}
                />
              ))}
            </div>
          </div>
        </>
      ) : data ? (
        <div className="alert alert-info pt-2 mt-2">
          {gettext("No satellite images are available for this location")}
        </div>
      ) : (
        <div className="text-center mt-5 mb-5">
          <Spinner animation="border" role="status">
            <span className="sr-only">Loading...</span>
          </Spinner>
        </div>
      )}
    </div>
  );
}

function GrowthTooltip(props) {
  const { active, payload, label } = props;
  if (!active || !payload || !payload[0]) return null;
  const data = payload[0].payload;
  return (
    <div className="growth-tooltip">
      <div className="text-muted">{moment.unix(label).format("DD-MM-YYYY")}</div>
      <table>
        <tbody>
          {getStatsTypes().map(
            (st) =>
              data[`${st}_average`] && (
                <tr>
                  <td>{st}:</td>
                  <td className={data.valid ? "text-green" : "text-error"}>
                    {cropr.toLocaleFixed(data[`${st}_average`], 2)}
                  </td>
                  <td>&plusmn; {cropr.toLocaleFixed(data[`${st}_std`], 2)}</td>
                </tr>
              )
          )}
        </tbody>
      </table>
    </div>
  );
}

function SatCard(props) {
  const dateKey = moment.unix(props.data.unix).format("YYYYMMDD");
  const myRef = useRef(null);
  useEffect(() => {
    if (props.selected) {
      myRef.current.scrollIntoView({ behavior: "smooth", inline: "center" });
    }
  }, [props.selected, props.fieldId]);
  return (
    <div ref={myRef} className={props.showYearR ? "b-r" : props.showYearL ? "b-l" : ""}>
      <div
        className={
          "font-weight-bold pr-2 pl-2 " +
          (props.showYearR ? "text-right " : "") +
          (props.showYearR || props.showYearL ? "text-primary" : "text-light")
        }
        style={{ height: "20px" }}
      >
        {" "}
        {props.showYearR || props.showYearL ? props.data.year : " "}
      </div>
      <div
        className={`m-1 p-2 bd-highlight card card-block w-80px  ${
          props.selected ? "border border-primary" : ""
        }`}
        onClick={() => props.onClick(dateKey)}
        role="button"
      >
        <div className="text-center">
          <img
            alt="ndvi"
            className="h-40px w-100"
            style={{ objectFit: "contain" }}
            src={`${window.origin}/api/v3/satellite/image/${props.fieldId}_${dateKey}_${props.imgType}.png`}
          />
        </div>
        <div className={props.data.valid ? "text-primary" : "text-error"}>
          {moment.unix(props.data.unix).format("D MMM")}
        </div>
      </div>
    </div>
  );
}

export default SatView;
